import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { preRegistrationApi } from '../api/pre-registration.api';
import { getApiErrorMessage, getApiErrorStatus } from '../utils/api-error.utils';
import { preRegistrationKeys } from './usePreRegistrationQueries';
import { PRE_REGISTRATION_STATUS_IDS } from '../utils/status-transitions.utils';
import type {
  PreRegisteredAccommodation,
  PreRegisteredAccommodationCreatePayload,
  PreRegisteredAccommodationPayload,
  PreRegistrationStatusChangePayload,
} from '../types/pre-registration.types';

// Rows change the list detail, the list counts and the accommodations search
const useInvalidateRows = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: preRegistrationKeys.municipalLists() });
    queryClient.invalidateQueries({ queryKey: preRegistrationKeys.accommodations() });
  };
};

const showError = (title: string, error: unknown, fallback: string) =>
  toast({ title, description: getApiErrorMessage(error, fallback), variant: 'destructive' });

export const useCreatePreRegisteredAccommodation = (listId: string) => {
  const invalidateRows = useInvalidateRows();
  return useMutation({
    mutationFn: (payload: PreRegisteredAccommodationCreatePayload) =>
      preRegistrationApi.createAccommodation(listId, payload),
    onSuccess: (row) => {
      invalidateRows();
      toast({
        title: row.accommodation ? 'Alojamiento agregado como Ya registrado' : 'Alojamiento agregado',
        description: row.accommodation
          ? `${row.establishmentName} — legajo ${row.accommodation.fileNumber}`
          : row.establishmentName,
      });
    },
    onError: (error) =>
      showError('Error al agregar el alojamiento', error, 'No se pudo agregar el alojamiento.'),
  });
};

export const useUpdatePreRegisteredAccommodation = () => {
  const invalidateRows = useInvalidateRows();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PreRegisteredAccommodationPayload }) =>
      preRegistrationApi.updateAccommodation(id, payload),
    onSuccess: (row) => {
      invalidateRows();
      toast({ title: 'Alojamiento actualizado', description: row.establishmentName });
    },
    onError: (error) => {
      // 409: the status changed meanwhile; reload so the row shows its current state
      if (getApiErrorStatus(error) === 409) invalidateRows();
      showError('Error al guardar el alojamiento', error, 'No se pudo guardar el alojamiento.');
    },
  });
};

interface UpdateAndLinkResult {
  row: PreRegisteredAccommodation;
  linkError?: unknown;
}

// Edit of a pending row with a picked match: save the data, then link it as "Ya registrado".
// A failed link keeps the saved data; it can be retried from the status menu.
export const useUpdateAndLinkPreRegisteredAccommodation = () => {
  const invalidateRows = useInvalidateRows();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
      accommodationId,
    }: {
      id: string;
      payload: PreRegisteredAccommodationPayload;
      accommodationId: string;
    }): Promise<UpdateAndLinkResult> => {
      const saved = await preRegistrationApi.updateAccommodation(id, payload);
      try {
        const row = await preRegistrationApi.changeAccommodationStatus(id, {
          statusId: PRE_REGISTRATION_STATUS_IDS.already_registered,
          accommodationId,
        });
        return { row };
      } catch (linkError) {
        return { row: saved, linkError };
      }
    },
    onSuccess: ({ row, linkError }) => {
      invalidateRows();
      if (linkError) {
        const reason = getApiErrorMessage(linkError, 'Error inesperado');
        toast({
          title: 'Datos guardados, pero no se pudo vincular',
          description: `${reason}. Podés vincularlo desde el menú de estado de la fila.`,
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'Alojamiento guardado como Ya registrado', description: row.establishmentName });
    },
    onError: (error) => {
      if (getApiErrorStatus(error) === 409) invalidateRows();
      showError('Error al guardar el alojamiento', error, 'No se pudo guardar el alojamiento.');
    },
  });
};

export const useChangePreRegistrationStatus = () => {
  const invalidateRows = useInvalidateRows();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PreRegistrationStatusChangePayload }) =>
      preRegistrationApi.changeAccommodationStatus(id, payload),
    onSuccess: (row) => {
      invalidateRows();
      toast({ title: `Estado: ${row.status.name}`, description: row.establishmentName });
    },
    onError: (error) => {
      if (getApiErrorStatus(error) === 409) invalidateRows();
      showError('Error al cambiar el estado', error, 'No se pudo cambiar el estado.');
    },
  });
};
